/*
 * Copyright (c) 2024 Helvia Technologies PC <contact@helvia.io>
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// Component
import { BackButton } from "@/components/UI/BackButton";
import { Button, Col, Form, Flex, Input, Row, Typography } from "antd";
import { CloseOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";

// misc
import { usePatchProject } from "@/api/projects";
import { useGetProject } from "@/api/projects";
import { NAV_PROJECTS, NAV_SUBSCRIPTION_PLANS } from "@/constants";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { ErrorsComponent } from "../UI/ErrorsComponent";
import { IRequestError } from "@/interfaces/ServerError.interface";
import { getFormattedDate } from "@/utils/date/getFormattedDate";
import { tokens as Tokens } from "@/utils/tokens";
import { useUserInfo } from "@/api/user";
import { queryClient } from "@/utils/queryClient";
import { ITokens } from "@/interfaces/User.interface";

type FieldType = Record<string, any>;

const { Text } = Typography;

const baseClassName = "screen-top-bar";

export const ScreenTopBar = () => {
  const tokensInstance = Tokens;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId, projectId } = useParams();

  const [isEditing, setIsEditing] = useState(false);

  const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);

  const { access: accessToken } = userAuth || {};
  const { data: userData } = useUserInfo(tokensInstance.getTokenSub(accessToken) as string);

  const params = {
    workspaceId: workspaceId as string,
    projectId: projectId as string,
  };
  // Mutation hook to patch service
  const {
    mutate: patchProject,
    isLoading: isSavingService,
    error: patchError,
  } = usePatchProject();

  // Fetch project data
  const { data: projectData } = useGetProject({
    ...params,
  });

  /**
   * Callback function for form submission.
   *
   * @param {Object} values - Form values
   */
  const onFinish = (values: Record<string, any>) => {
    patchProject(
      {
        ...params,
        projectData: { title: values.title },
      },
      {
        onSuccess: () => setIsEditing(false),
      }
    );
  };

  // USE EFFECTS
  useEffect(() => {
    if (!projectData) {
      return;
    }
    form.setFieldValue("title", projectData.title);
  }, [form, projectData]);

  return (
    <>
      <Flex
        className={baseClassName}
        align="center"
        justify="space-between"
        wrap="wrap"
        gap="middle"
      >
        <Flex align="center">
          <BackButton
            to={
              location.state?.canGoBack
                ? -1
                : replaceUrlParams(NAV_PROJECTS, {
                    workspaceId: workspaceId as string,
                    projectId: projectId as string,
                  })
            }
          />
          <Form
            name="project-title-form"
            className="project-title-form"
            autoComplete="off"
            form={form} // Add the form instance
            onFinish={onFinish}
          >
            {!isEditing ? (
              <Flex wrap="nowrap" gap={3}>
                <Typography.Text
                  className={`${baseClassName}__project-title`}
                  title={projectData?.title}
                >
                  {projectData?.title}
                </Typography.Text>
                <a
                  style={{ marginBottom: 0, lineHeight: "133%" }}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsEditing(true);
                  }}
                >
                  <EditOutlined />
                </a>
              </Flex>
            ) : (
              <Flex>
                <Form.Item<FieldType>
                  className="m-0 mr-3"
                  name="title"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item className="m-0 float-right">
                  <Button
                    className="mr-2"
                    type="default"
                    shape="circle"
                    icon={<CloseOutlined style={{ fontSize: ".825rem" }} />}
                    onClick={() => setIsEditing(false)}
                  />
                  <Button
                    loading={isSavingService}
                    type="primary"
                    shape="circle"
                    icon={<SaveOutlined style={{ fontSize: ".825rem" }} />}
                    htmlType="submit"
                  />
                </Form.Item>
              </Flex>
            )}
          </Form>
        </Flex>
        <Flex align="center">
          <p className="mb-0 mr-3">
            {t("project_single.subscription_plan")}:{" "}
            <strong>{projectData?.subscription_plan}</strong>
            {
              projectData?.is_trial
              ? <div>
                <Text type="success">&nbsp;{t("project_single.in_trial_ext", {date: getFormattedDate(projectData?.pending_subscription_cancellation_date, userData?.timezone,"DD-MM-YYYY")})}</Text>
              </div>
              : null
            }
          </p>

          <Button
            type="primary"
            onClick={(e) => {
              e.preventDefault();
              navigate(
                `${replaceUrlParams(NAV_SUBSCRIPTION_PLANS, {
                  ...params,
                })}`, { state: { canGoBack: true } }
              );
            }}
          >
            {t("common.change_plan")}
          </Button>
        </Flex>
      </Flex>
      {/* Patch Project Title Error */}
      <Row justify="end">
        <Col>
          <ErrorsComponent error={patchError as unknown as IRequestError} />
        </Col>
      </Row>
      {/* ./Patch Project Title Error */}
    </>
  );
};
